// Content cards library: a tiny reactive store shared between the
// AddCardModal (count badge on the Content cards tab) and the
// ContentCardBrowser (list and click-to-select). One fetch per page
// session is enough for v1; both consumers ride the same call.
//
// This replaces the Step-1-era contentCardsFixture.js, which used
// fictional ids (101–303) that don't exist in Dr. Ken's real DB. The
// fixture is intentionally NOT removed — it stays in the tree as
// reference shape data and is no longer imported by either consumer.

import { reactive } from 'vue';
import apiService from '@/services/apiService';

export const contentCardsLibraryState = reactive({
  items: [],            // raw content_cards rows (id, type, blocks, status, sort_order, ...)
  loaded: false,        // true after the first successful fetch
  loading: false,       // in-flight indicator for the UI
  loadError: null,      // last error message or null
});

let inFlight = null;

/**
 * Fetch /cards/list if we haven't yet, or force a refetch when
 * `force` is true. Multiple concurrent callers share the same promise
 * so we never fire duplicate requests on the same mount tick.
 *
 * Returns the items array on success.
 */
export async function loadContentCardsLibrary({ force = false } = {}) {
  if (!force && contentCardsLibraryState.loaded) {
    return contentCardsLibraryState.items;
  }
  if (inFlight) return inFlight;

  contentCardsLibraryState.loading = true;
  contentCardsLibraryState.loadError = null;
  inFlight = (async () => {
    try {
      const rows = await apiService.fetchContentCards();
      // Replace in place so reactive consumers see the update.
      contentCardsLibraryState.items.splice(
        0, contentCardsLibraryState.items.length, ...rows,
      );
      contentCardsLibraryState.loaded = true;
      return rows;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      contentCardsLibraryState.loadError = typeof detail === 'string'
        ? detail
        : 'Failed to load content cards.';
      throw e;
    } finally {
      contentCardsLibraryState.loading = false;
      inFlight = null;
    }
  })();
  return inFlight;
}
